import {
  Box,
  CircularProgress,
  Paper,
  Typography,
  Link,
  alpha,
  IconButton,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import colors from "../../Constants/colors";
import { enqueueSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import BusinessIcon from "@mui/icons-material/Business";
import HomeIcon from "@mui/icons-material/Home";
import RoomIcon from "@mui/icons-material/Room";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ArrowBack from "@mui/icons-material/ArrowBack";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import LanguageIcon from "@mui/icons-material/Language";
import ArrowForward from "@mui/icons-material/ArrowForward";
import { entityIcons } from "../../Constants/entityIcons";
import { getEntityIconByName } from "../../Helpers/getEntityIcon";
import { useCreateCompany } from "../../Query/useCompanies";
import { useTerminology } from "../../Core/useTerminology";
import { uploadFile, uploadWebsite } from "../../Api/company";
import FileUploader from "../FileUpload/FileUploader";
import {
  allowedExtensions,
  MAX_FILE_SIZE,
} from "../../Constants/fileExtensions";
import { ComoButton } from "../Common/ComoButton";
import { ComoInput } from "../Common/ComoInput";
import { ComoSelect } from "../Common/ComoSelect";

const industryKeys = [
  "technology",
  "healthcare",
  "finance",
  "education",
  "manufacturing",
  "retail",
  "realEstate",
  "transportation",
  "foodBeverage",
  "entertainment",
  "nonProfit",
  "other",
];

const steps = ["basicInfo", "documents", "websites"];

const CreateCompanyForm: React.FC = () => {
  const [activeStep, setActiveStep] = useState<number>(0);
  const [companyName, setCompanyName] = useState("");
  const [companyAbout, setCompanyAbout] = useState("");
  const [industry, setIndustry] = useState("technology");
  const [location, setLocation] = useState("");
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [pendingWebsites, setPendingWebsites] = useState<string[]>([]);
  const [websiteInput, setWebsiteInput] = useState("");
  const [isUploadingDocuments, setIsUploadingDocuments] = useState(false);
  const [isUploadingWebsites, setIsUploadingWebsites] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const terminology = useTerminology();
  const [selectedIconKey, setSelectedIconKey] = useState("officeBuilding");

  const createCompanyMutation = useCreateCompany();
  const { t } = useTranslation("createCompanyForm");

  const getIconIdByKey = (key: string) =>
    entityIcons.findIndex((icon) => icon.key === key) + 1;

  const handleRemovePendingFile = (fileName: string) => {
    setPendingFiles((prev) => prev.filter((f) => f.name !== fileName));
  };

  const handleAddWebsite = () => {
    if (websiteInput.trim() && !pendingWebsites.includes(websiteInput.trim())) {
      setPendingWebsites((prev) => [...prev, websiteInput.trim()]);
      setWebsiteInput("");
    }
  };

  const handleRemoveWebsite = (website: string) => {
    setPendingWebsites((prev) => prev.filter((w) => w !== website));
  };

  const handleNext = () => {
    if (activeStep === 0) {
      if (!companyName.trim()) {
        enqueueSnackbar(
          t("createCompanyForm.tooltipIncomplete", {
            entity: terminology.singular,
          }),
          { variant: "warning" },
        );
        return;
      }
      if (companyName.length > 40) {
        enqueueSnackbar(t("createCompanyForm.nameTooLong"), {
          variant: "warning",
        });
        return;
      }
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleCreateCompany = async () => {
    if (companyName.length > 40) {
      enqueueSnackbar(t("createCompanyForm.nameTooLong"), {
        variant: "warning",
      });
      return;
    }

    const oversized = pendingFiles.find((f) => f.size > MAX_FILE_SIZE);
    if (oversized) {
      const sizeMB = MAX_FILE_SIZE / (1024 * 1024);
      enqueueSnackbar(`"${oversized.name}" exceeds the ${sizeMB} MB limit`, {
        variant: "error",
      });
      return;
    }

    const icon_id = getIconIdByKey(selectedIconKey);
    const industryName = t(`createCompanyForm.industries.${industry}`);

    createCompanyMutation.mutate(
      {
        name: companyName,
        about: companyAbout.trim(),
        industry: industryName,
        location: location.trim(),
        icon_id,
      },
      {
        onSuccess: async (response) => {
          const companyId = String(response.data.company.id);

          if (pendingFiles.length > 0) {
            setIsUploadingDocuments(true);
            const uploads = pendingFiles.map(async (file) => {
              const formData = new FormData();
              formData.append("company_id", companyId);
              formData.append("document_name", file.name);
              formData.append("file", file);
              return uploadFile(formData);
            });
            await Promise.allSettled(uploads);
            queryClient.invalidateQueries({
              queryKey: ["documents", companyId],
            });
            setIsUploadingDocuments(false);
          }

          if (pendingWebsites.length > 0) {
            setIsUploadingWebsites(true);
            const websiteUploads = pendingWebsites.map(async (website) => {
              return uploadWebsite(companyId, website, website);
            });
            await Promise.allSettled(websiteUploads);
            queryClient.invalidateQueries({
              queryKey: ["websites", companyId],
            });
            setIsUploadingWebsites(false);
          }

          navigate(`/entity/${companyId}`);
        },
      },
    );
  };

  const isFormComplete = companyName.trim().length > 0;
  const isBusy =
    createCompanyMutation.isPending ||
    isUploadingDocuments ||
    isUploadingWebsites;

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: colors.gray50 }}>
      <Box
        sx={{
          width: "100%",
          maxWidth: 1280,
          mx: "auto",
          px: { xs: 2, sm: 3 },
          py: 4,
          overflowX: "auto",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 2,
            border: `1px solid ${colors.gray200}`,
            borderLeft: `4px solid ${colors.primary_dark}`,
            backgroundColor: colors.white,
          }}
        >
          <Link
            component="button"
            underline="none"
            onClick={() => navigate("/entity")}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              color: colors.gray600,
              fontSize: "0.875rem",
              cursor: "pointer",
              mb: 1,
              textAlign: "left",
              "&:hover": { color: colors.primary_dark },
            }}
          >
            <ArrowBack sx={{ fontSize: 16 }} />
            {t("createCompanyForm.backToEntities", {
              entity: terminology.plural,
            })}
          </Link>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
              color: colors.primary_dark,
              mt: 0.5,
              textAlign: "left",
            }}
          >
            {t("createCompanyForm.createEntityTitle", {
              entity: terminology.singular,
            })}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: colors.gray500, mt: 0.5, textAlign: "left" }}
          >
            {t("createCompanyForm.subtitle", {
              entity: terminology.singular,
            })}
          </Typography>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 2,
            border: `1px solid ${colors.gray200}`,
            backgroundColor: colors.white,
          }}
        >
          <Stepper
            activeStep={activeStep}
            sx={{
              mb: 2,
              flexWrap: "wrap",
              rowGap: 2,
              "& .MuiStepLabel-root .MuiStepLabel-label": {
                color: `${colors.gray700} !important`,
                fontWeight: 500,
              },
              "& .MuiStepLabel-root.Mui-active .MuiStepLabel-label": {
                color: `${colors.primary_dark} !important`,
                fontWeight: 600,
              },
              "& .MuiStepLabel-root.Mui-completed .MuiStepLabel-label": {
                color: `${colors.accent} !important`,
                fontWeight: 500,
              },
              "& .MuiStepIcon-root": {
                color: colors.gray400,
                fontSize: "1.75rem",
              },
              "& .MuiStepIcon-root.Mui-active": {
                color: colors.primary_dark,
              },
              "& .MuiStepIcon-root.Mui-completed": {
                color: colors.accent,
              },
              "& .MuiStepIcon-text": {
                fill: colors.white,
                fontWeight: 600,
              },
              "& .MuiStepConnector-line": {
                borderColor: colors.gray300,
                borderTopWidth: 2,
              },
              "& .MuiStepConnector-root.Mui-active .MuiStepConnector-line": {
                borderColor: colors.primary_dark,
              },
              "& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line": {
                borderColor: colors.accent,
              },
            }}
          >
            <Step>
              <StepLabel
                onClick={() => setActiveStep(0)}
                sx={{
                  cursor: "pointer",
                }}
              >
                {t("createCompanyForm.step1", { entity: terminology.singular })}
              </StepLabel>
            </Step>
            <Step>
              <StepLabel
                onClick={() => {
                  if (isFormComplete) {
                    setActiveStep(1);
                  }
                }}
                sx={{
                  cursor: isFormComplete ? "pointer" : "default",
                }}
              >
                {t("createCompanyForm.step2")}
              </StepLabel>
            </Step>
            <Step>
              <StepLabel
                onClick={() => {
                  if (isFormComplete) {
                    setActiveStep(2);
                  }
                }}
                sx={{
                  cursor: isFormComplete ? "pointer" : "default",
                }}
              >
                {t("createCompanyForm.step3")}
              </StepLabel>
            </Step>
          </Stepper>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 2,
            border: `1px solid ${colors.gray200}`,
            borderLeft: `4px solid ${colors.primary_dark}`,
            backgroundColor: colors.white,
          }}
        >
          {activeStep === 0 && (
            <>
              <Box sx={{ mb: 4, textAlign: "left" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 1,
                    mb: 2,
                  }}
                >
                  <BusinessIcon
                    sx={{ color: colors.primary_dark, fontSize: "1rem" }}
                  />
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: colors.primary_dark,
                      fontSize: "0.9rem",
                    }}
                  >
                    {t("createCompanyForm.chooseIcon")}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 2,
                    flexWrap: "wrap",
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 1.5,
                      backgroundColor: colors.gray100,
                      border: `1px solid ${colors.gray300}`,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      color: colors.accent,
                      "& .MuiSvgIcon-root": { fontSize: 20 },
                    }}
                  >
                    {getEntityIconByName(
                      entityIcons.find((i) => i.key === selectedIconKey)
                        ?.iconName || "",
                    )}
                  </Box>
                  <Typography
                    sx={{
                      color: colors.gray700,
                      fontWeight: 500,
                      fontSize: "0.875rem",
                    }}
                  >
                    {t(`createCompanyForm.entityIcons.${selectedIconKey}`)}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.75,
                  }}
                >
                  {entityIcons.map((entry) => (
                    <Box
                      component="button"
                      type="button"
                      key={entry.key}
                      onClick={() => setSelectedIconKey(entry.key)}
                      sx={{
                        width: 36,
                        height: 36,
                        flexShrink: 0,
                        border:
                          selectedIconKey === entry.key
                            ? `2px solid ${colors.accent}`
                            : `1px solid ${colors.gray200}`,
                        borderRadius: 1,
                        cursor: "pointer",
                        backgroundColor:
                          selectedIconKey === entry.key
                            ? alpha(colors.accent, 0.08)
                            : colors.gray50,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        color:
                          selectedIconKey === entry.key
                            ? colors.accent
                            : colors.gray500,
                        "& .MuiSvgIcon-root": { fontSize: 20 },
                        "&:hover": {
                          backgroundColor:
                            selectedIconKey === entry.key
                              ? alpha(colors.accent, 0.12)
                              : colors.gray100,
                          borderColor:
                            selectedIconKey === entry.key
                              ? colors.accent
                              : colors.gray300,
                        },
                      }}
                    >
                      {getEntityIconByName(entry.iconName)}
                    </Box>
                  ))}
                </Box>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                  textAlign: "left",
                }}
              >
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      mb: 1,
                    }}
                  >
                    <HomeIcon
                      sx={{ color: colors.primary_dark, fontSize: "1rem" }}
                    />
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: colors.primary_dark,
                        fontSize: "0.875rem",
                      }}
                    >
                      {t("createCompanyForm.companyNameLabel", {
                        entity: terminology.genitive,
                      })}{" "}
                      *
                    </Typography>
                  </Box>
                  <ComoInput
                    disabled={isBusy}
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder={t("createCompanyForm.namePlaceholder", {
                      entity: terminology.singular,
                    })}
                    inputProps={{ maxLength: 40 }}
                  />
                </Box>

                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      mb: 1,
                    }}
                  >
                    <BusinessCenterIcon
                      sx={{ color: colors.primary_dark, fontSize: "1rem" }}
                    />
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: colors.primary_dark,
                        fontSize: "0.875rem",
                      }}
                    >
                      {t("createCompanyForm.industryLabel")}
                    </Typography>
                  </Box>
                  <ComoSelect
                    options={industryKeys.map((key) => ({
                      id: key,
                      label: t(`createCompanyForm.industries.${key}`),
                    }))}
                    selectedOption={
                      industryKeys.find((key) => key === industry)
                        ? {
                            id: industry,
                            label: t(
                              `createCompanyForm.industries.${industry}`,
                            ),
                          }
                        : undefined
                    }
                    onSelect={(option) => setIndustry(option.id as string)}
                    placeholder={t("createCompanyForm.industryLabel")}
                    disabled={() => isBusy}
                    sx={{ mb: 0 }}
                  />
                </Box>

                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      mb: 1,
                    }}
                  >
                    <RoomIcon
                      sx={{ color: colors.primary_dark, fontSize: "1rem" }}
                    />
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: colors.primary_dark,
                        fontSize: "0.875rem",
                      }}
                    >
                      {t("createCompanyForm.locationLabel")}
                    </Typography>
                  </Box>
                  <ComoInput
                    disabled={isBusy}
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder={t("createCompanyForm.locationPlaceholder")}
                  />
                </Box>

                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      mb: 1,
                    }}
                  >
                    <InfoOutlinedIcon
                      sx={{ color: colors.primary_dark, fontSize: "1rem" }}
                    />
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: colors.primary_dark,
                        fontSize: "0.875rem",
                      }}
                    >
                      {t("createCompanyForm.aboutLabel")}
                    </Typography>
                  </Box>
                  <ComoInput
                    multiline
                    rows={4}
                    disabled={isBusy}
                    value={companyAbout}
                    onChange={(e) => setCompanyAbout(e.target.value)}
                    placeholder={t("createCompanyForm.aboutPlaceholder", {
                      entity: terminology.singular,
                    })}
                  />
                </Box>
              </Box>
            </>
          )}

          {activeStep === 1 && (
            <Box sx={{ textAlign: "left" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  mb: 2,
                }}
              >
                <CloudUploadOutlinedIcon
                  sx={{ color: colors.primary_dark, fontSize: "1rem" }}
                />
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: colors.primary_dark,
                    fontSize: "0.9rem",
                  }}
                >
                  {t("createCompanyForm.attachDocumentsTitle")}
                </Typography>
              </Box>
              <Typography
                variant="body2"
                sx={{
                  display: "block",
                  color: colors.gray500,
                  mb: 2,
                  fontSize: "0.875rem",
                }}
              >
                {t("createCompanyForm.attachDocumentsHint")}
              </Typography>
              <FileUploader
                uploadedFiles={pendingFiles}
                setUploadedFiles={setPendingFiles}
                allowedExtensions={allowedExtensions}
                disabled={isBusy}
                showUploadImage={false}
                text={true}
              />
              {pendingFiles.length > 0 && (
                <Box
                  sx={{
                    mt: 2,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.75,
                  }}
                >
                  {pendingFiles.map((file) => (
                    <Box
                      key={`${file.name}-${file.size}`}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        backgroundColor: alpha(colors.accent, 0.08),
                        borderRadius: 1,
                        border: `1px solid ${
                          colors.orange50 ?? colors.gray200
                        }`,
                        py: 1,
                        px: 1.5,
                      }}
                    >
                      <UploadFileIcon
                        sx={{ color: colors.accent, fontSize: 20, mr: 1 }}
                      />
                      <Typography
                        sx={{
                          flex: 1,
                          fontSize: "0.8rem",
                          fontWeight: 500,
                          color: colors.gray900,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {file.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "0.7rem",
                          color: colors.gray600,
                          mr: 1,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {(file.size / (1024 * 1024)).toFixed(2)} MB
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleRemovePendingFile(file.name)}
                        disabled={isBusy}
                        sx={{
                          p: 0.5,
                          "&:hover": { backgroundColor: colors.red50 },
                        }}
                      >
                        <CloseIcon
                          sx={{ fontSize: 16, color: colors.red600 }}
                        />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}

          {activeStep === 2 && (
            <Box sx={{ textAlign: "left" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  mb: 2,
                }}
              >
                <LanguageIcon
                  sx={{ color: colors.primary_dark, fontSize: "1rem" }}
                />
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: colors.primary_dark,
                    fontSize: "0.9rem",
                  }}
                >
                  {t("createCompanyForm.attachWebsitesTitle")}
                </Typography>
              </Box>
              <Typography
                variant="body2"
                sx={{
                  display: "block",
                  color: colors.gray500,
                  mb: 2,
                  fontSize: "0.875rem",
                }}
              >
                {t("createCompanyForm.attachWebsitesHint")}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 1,
                  mb: 2,
                }}
              >
                <ComoInput
                  placeholder="https://example.com"
                  value={websiteInput}
                  onChange={(e) => setWebsiteInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      handleAddWebsite();
                    }
                  }}
                  disabled={isBusy}
                  sx={{ flex: 1 }}
                />
                <ComoButton
                  secondary
                  onClick={handleAddWebsite}
                  disabled={!websiteInput.trim() || isBusy}
                  sx={{
                    minWidth: 120,
                    backgroundColor: colors.primary_dark,
                    color: colors.white,
                    "&:hover": {
                      backgroundColor: alpha(colors.primary_dark, 0.8),
                    },
                    "&.Mui-disabled": {
                      backgroundColor: colors.gray400,
                      color: colors.white,
                    },
                  }}
                >
                  {t("createCompanyForm.addWebsite")}
                </ComoButton>
              </Box>
              {pendingWebsites.length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.75,
                  }}
                >
                  {pendingWebsites.map((website) => (
                    <Box
                      key={website}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        backgroundColor: alpha(colors.accent, 0.08),
                        borderRadius: 1,
                        border: `1px solid ${
                          colors.orange50 ?? colors.gray200
                        }`,
                        py: 1,
                        px: 1.5,
                      }}
                    >
                      <LanguageIcon
                        sx={{ color: colors.accent, fontSize: 20, mr: 1 }}
                      />
                      <Typography
                        sx={{
                          flex: 1,
                          fontSize: "0.8rem",
                          fontWeight: 500,
                          color: colors.gray900,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {website}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveWebsite(website)}
                        disabled={isBusy}
                        sx={{
                          p: 0.5,
                          "&:hover": { backgroundColor: colors.red50 },
                        }}
                      >
                        <CloseIcon
                          sx={{ fontSize: 16, color: colors.red600 }}
                        />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              justifyContent: activeStep === 0 ? "flex-end" : "space-between",
              mt: 4,
              pt: 3,
              borderTop: `1px solid ${colors.gray200}`,
            }}
          >
            {activeStep > 0 && (
              <ComoButton
                secondary
                onClick={handleBack}
                disabled={isBusy}
                startIcon={<ArrowBack />}
                sx={{
                  backgroundColor: colors.primary_dark,
                  color: colors.white,
                  "&:hover": {
                    backgroundColor: alpha(colors.primary_dark, 0.8),
                  },
                  "&.Mui-disabled": {
                    backgroundColor: colors.gray400,
                    color: colors.white,
                  },
                }}
              >
                {t("createCompanyForm.back")}
              </ComoButton>
            )}
            {activeStep < steps.length - 1 ? (
              <ComoButton
                onClick={handleNext}
                endIcon={<ArrowForward />}
                disabled={isBusy}
                sx={{
                  backgroundColor: colors.accent,
                  color: colors.white,
                  "&:hover": {
                    backgroundColor: alpha(colors.accent, 0.8),
                  },
                  "&.Mui-disabled": {
                    backgroundColor: colors.btn_dark_disabled,
                    color: colors.white,
                  },
                }}
              >
                {t("createCompanyForm.next")}
              </ComoButton>
            ) : (
              <ComoButton
                onClick={handleCreateCompany}
                disabled={isBusy}
                variant="modern"
              >
                {isBusy ? (
                  <CircularProgress
                    size={24}
                    sx={{ color: colors.primary_dark }}
                  />
                ) : (
                  t("createCompanyForm.createButton", {
                    entity: terminology.singular,
                  })
                )}
              </ComoButton>
            )}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default CreateCompanyForm;
